import React, { useCallback } from 'react';

import {
  Button, Flex, HStack, Menu, MenuButton, MenuItem, MenuList, Stack, Text, useBoolean,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';

import { Icon } from 'components';
import sections from 'constants/sidebar';
import { AccessTypes } from 'enums/types';
import { policy } from 'polices';

import CompanyCard from './companies';
import { addWidthVariant, autoHeightVariant, rotateX180Variant } from './constants';

function MenuSubItem({ item }) {
  const [focused, setFocused] = useBoolean(false);
  const { push } = useHistory();

  return (
    <MenuItem
      _focus={null}
      _active={null}
      onFocus={setFocused.on}
      onBlur={setFocused.off}
      onClick={() => push({
        key: item.label,
        pathname: item.pathname,
      })}
      icon={focused && (
        <Icon
          color="white"
          name="caret-right"
          family="fas"
          size="sm"
        />
      )}>
      <Text
        color="white"
        variant={focused ? 'xSmallBold' : 'xSmallRegular'}>
        {item.label}
      </Text>
    </MenuItem>
  );
}

function SubItems({ state, isOpen, options }) {
  const MenuSubItems = useCallback(() => (
    <>
      {options.map((item) => (
        <MenuSubItem
          key={`sub-item-${item.label}`}
          item={item}
        />
      ))}
    </>
  ), [options]);

  if (state === 'short') {
    return (
      <MenuList
        bg="primary.700"
        border={0}
        shadow={5}>
        <MenuSubItems />
      </MenuList>
    );
  }
  return (
    <Stack
      as={motion.div}
      variants={autoHeightVariant}
      display={state === 'full' && 'none'}
      overflow="hidden"
      animate={isOpen.toString()}
      bg="primary.700"
      position="relative"
      top={-1}
      color="white"
      borderBottomRadius={5}
      py={isOpen && 2}
      px={isOpen && 4}>
      <MenuSubItems />
    </Stack>
  );
}

function Item({ state, item }) {
  const { push, location } = useHistory();
  const isSelected = [
    item.pathname,
    ...(item.options ?? []).map(({ pathname }) => pathname),
  ].includes(location.pathname);
  if (item.policy) {
    const [content, method] = item.policy?.split('.');
    const access = policy(content)(method);
    if (![AccessTypes.Allow, AccessTypes.Related].includes(access)) {
      return null;
    }
  }
  let options;
  if (item.options) {
    options = item.options.filter((option) => {
      if (option.policy) {
        const [content, method] = option.policy?.split('.');
        return [AccessTypes.Allow, AccessTypes.Related].includes(policy(content)(method));
      }
      return true;
    });
    if (isEmpty(options)) {
      return null;
    }
  }

  return (
    <Menu placement="right-end">
      {({ isOpen }) => (
        <>
          <MenuButton
            as={Button}
            _focus={{}}
            _active={{}}
            _hover={null}
            color="white"
            bg={isSelected ? 'primary.700' : 'transparent'}
            onClick={() => {
              if (item.pathname) {
                push({
                  key: item.label,
                  pathname: item.pathname,
                  state: { item },
                });
              }
            }}
            px={3}>
            <Flex
              w="100%"
              alignItems="center"
              justifyContent="flex-start">
              <Icon
                name={item.icon}
                family="fas"
                size="sm"
              />
              <motion.div
                animate={state}
                variants={addWidthVariant}
                transition={{ duration: 0.5 }}>
                <HStack
                  alignItems="center"
                  justifyContent="space-between">
                  <Text
                    ml={4}
                    textAlign="start"
                    variant={isOpen ? 'smallBold' : 'smallSemiBold'}
                    isTruncated>
                    {item.label}
                  </Text>
                  <motion.div
                    animate={isOpen ? 'full' : 'short'}
                    variants={rotateX180Variant}
                    transition={{ duration: 0.5 }}>
                    <Icon
                      hidden={state === 'short' || !item.options}
                      name="chevron-down"
                      family="fas"
                      size="xs"
                    />
                  </motion.div>
                </HStack>
              </motion.div>
            </Flex>
          </MenuButton>
          {!!options && (
            <SubItems
              isOpen={isOpen}
              state={state}
              options={options}
            />
          )}
        </>
      )}
    </Menu>
  );
}

function Section({ state, section }) {
  return (
    <>
      <Text
        overflow="hidden"
        isTruncated
        px={3}
        hidden={!section.label || state === 'short'}
        color="white"
        variant="2xSmallSemiBold">{section.label}
      </Text>
      <Flex
        hidden={!section.label || state === 'full'}
        w="100%"
        h="1px"
        bg="primary.transparent"
      />
      <Flex
        flexDir="column"
        px={3}>
        {section.items.map((item) => (
          <Item
            key={`sidebar-item-${item.label}`}
            item={item}
            state={state}
          />
        ))}
      </Flex>
    </>
  );
}

export default function Options({ state }) {
  return (
    <Stack
      flexDir="column"
      w="100%"
      py={6}>
      <Flex
        flexDir="column"
        px={3}>
        <CompanyCard state={state} />
      </Flex>
      {sections.map((section, index) => (
        <Section
          key={`sidebar-${index}`}
          section={section}
          state={state}
        />
      ))}
    </Stack>
  );
}
